import type { Task } from '../api/models/task';
import type { EventKind } from '../api/models/eventKind';
import { isEventDeliveredTask } from './tasks/isEventDeliveredTask';

/**
 * Resolve EventKind.requiresEvaluation when the catalogue omits the flag.
 * Non-oral kinds default false; lecturer-started oral kinds default true.
 */
export const resolveEventKindRequiresEvaluation = (eventKind: EventKind): boolean => {
  if (eventKind.requiresEvaluation !== undefined) {
    return eventKind.requiresEvaluation;
  }
  if (eventKind.kind === 'oral_exam') {
    return eventKind.startAuthority !== 'self_service';
  }
  return false;
};

/**
 * Resolve EventKind.requiresRecording when the catalogue omits the flag.
 * Non-oral kinds default false; lecturer-started oral kinds default true.
 */
export const resolveEventKindRequiresRecording = (eventKind: EventKind): boolean => {
  if (eventKind.requiresRecording !== undefined) {
    return eventKind.requiresRecording;
  }
  if (eventKind.kind === 'oral_exam') {
    return eventKind.startAuthority !== 'self_service';
  }
  return false;
};

/**
 * Determines if a task requires evaluation.
 *
 * @param task - The task to check
 * @param eventKinds - Array of available event kinds
 * @returns boolean indicating if the task should be graded
 */
export const taskRequiresEvaluation = (task: Task, eventKinds: EventKind[]): boolean => {
  // Activity tasks are mark-as-done only; lecturers do not score them.
  if (task.type === 'activity') {
    return false;
  }

  // For event-delivered tasks (event, oral_exam, MCQ-as-event), defer to the
  // EventKind's requiresEvaluation field.
  if (isEventDeliveredTask(task) && task.eventConfig?.eventKindId) {
    const eventKind = eventKinds.find((k) => k.id === task.eventConfig?.eventKindId);
    if (!eventKind) {
      return false;
    }
    return resolveEventKindRequiresEvaluation(eventKind);
  }

  // For non-event tasks, they are always relevant for now
  return true;
};

/**
 * Whether a task is AI-tutored, i.e. evaluated automatically by the AI tutor
 * with no manual lecturer feedback required.
 *
 * Mirrors the signal used by EvaluationCard.needsFeedback and ElementHeader.isAutoEval.
 */
export const isTaskAiTutored = (task: Task): boolean => task.evaluation?.mode === 'auto';

/**
 * Whether the lecturer module-save / submit gate owns this task.
 * AI-tutored essays stay sealed by the tutor and must not be sent or completeness-checked.
 */
export const isLecturerScoredTask = (task: Task, eventKinds: EventKind[]): boolean => (
  taskRequiresEvaluation(task, eventKinds) && !isTaskAiTutored(task)
);

/**
 * Whether a task can be hidden from the lecturer EvaluationDesk because it
 * carries no graded feedback for the lecturer to provide.
 *
 * Hides:
 * - AI-tutored tasks (auto-evaluated, no manual feedback)
 * - Pure event tasks (`type === 'event'`: meetings/calendar entries with no
 *   grading; oral exams use `type === 'oral_exam'` and stay visible)
 * MCQ (`multiple_choice`) and `oral_exam` tasks remain visible.
 */
export const isTaskHiddenWhenAiTutoredFiltered = (task: Task): boolean => (
  isTaskAiTutored(task) || task.type === 'event'
);
